/**
 * FileStore adapter backed by a Jazz DiagramProject, and the
 * `CollaborativeTextProvider` for the same files.
 *
 * Two storage shapes coexist here, per file:
 *
 * - **Collaborative** — the file has a `TextUpdate` log. Content lives in a
 *   Yjs document materialised from that log, and the editor binds CodeMirror
 *   straight to it, so two writers' edits merge character-by-character.
 * - **Legacy** — the file predates the log and nobody with write access has
 *   opened it yet. Content is the original `co.plainText()`, written whole
 *   with `applyDiff`. Readers stay here permanently: bootstrapping a log is
 *   a write.
 *
 * A writer opening a legacy file promotes it (see `ensureUpdateLog`). After
 * that the plain text is a derived mirror, refreshed on a debounce so
 * readers that predate the log — a rolled-back deploy, `forkProject`, the
 * FUSE mirror until it becomes a Yjs client — keep seeing current content.
 * Phase D deletes the mirror along with the field.
 *
 * The Jazz subscription is self-managing: it starts when the first
 * onFileChange listener registers and stops when the last one unsubscribes.
 */
import type {
  CollaborativeTextProvider,
  FileStore,
  Unsubscribe,
} from "@diagram/editor";
import { CollaborativeDocument, Compactor } from "@diagram/collab-text";
import { co, type Group } from "jazz-tools";
import type { Text as YText } from "yjs";
import {
  FileEntry,
  JazzUpdateLog,
  createTextUpdateLog,
  type DiagramProject,
} from "./schema";

type FileChangeCallback = (path: string, content: string) => void;
type FileDeletedCallback = (path: string) => void;

/**
 * How long changes to a collaborative document are coalesced before they
 * reach the `FileStore`'s listeners and the legacy mirror.
 *
 * Yjs emits an operation per keystroke. The consumers on the other side —
 * a markdown preview rebuild, a TypeScript worker sync, a CRDT write to the
 * mirror — are all far more expensive than that, and none of them needs
 * per-character latency.
 */
const FLUSH_DELAY_MS = 150;

/**
 * Log size at which a file's history is compacted into a snapshot.
 *
 * One Yjs operation per keystroke means an hour of typing is thousands of
 * `TextUpdate` CoValues, all of which a new reader has to load. A few
 * hundred entries is small enough to sync quickly and large enough that
 * compaction is rare.
 */
const COMPACTION_THRESHOLD = 300;

/**
 * How long a snapshot sits in the log before the entries it supersedes are
 * deleted. Sized for "every open tab has caught up" over ordinary sync, with
 * room to spare — over-retaining costs a little history, deleting too early
 * shows someone a truncated document until the snapshot reaches them.
 */
const COMPACTION_GRACE_MS = 120_000;

/** A file whose content lives in a Yjs document. */
interface CollaborativeFile {
  /** Id of the `TextUpdate` list this document reads. */
  listId: string;
  log: JazzUpdateLog;
  document: CollaborativeDocument;
  compactor: Compactor;
  detach: Unsubscribe[];
  flushTimer: ReturnType<typeof setTimeout> | null;
  /** Whether this process caused any of the changes awaiting a flush. */
  hasLocalChange: boolean;
}

export class JazzFileStoreAdapter
  implements FileStore, CollaborativeTextProvider
{
  private readonly changeCallbacks: FileChangeCallback[] = [];
  private readonly createdCallbacks: FileChangeCallback[] = [];
  private readonly deletedCallbacks: FileDeletedCallback[] = [];
  private readonly lastSyncedContent = new Map<string, string>();
  private readonly collaborative = new Map<string, CollaborativeFile>();
  private unsubscribeJazz: (() => void) | null = null;

  constructor(
    private project: DiagramProject,
    private readonly group: Group,
  ) {}

  /**
   * Point the adapter at a newer wrapper for the same project.
   *
   * The adapter is cached by the file list's id so that its documents,
   * subscriptions and timers outlive the wrapper churn Jazz produces on every
   * write. The wrapper itself is not durable, though, so the cache hands over
   * the current one instead of leaving the adapter reading the one it was
   * built with. Everything stateful is keyed by path and unaffected.
   */
  adoptProject(project: DiagramProject): void {
    this.project = project;
  }

  listFiles(): string[] {
    const files = this.project.files;
    if (!files) return [];
    const paths: string[] = [];
    for (let i = 0; i < files.length; i++) {
      const entry = files[i];
      if (entry?.path) paths.push(entry.path);
    }
    return paths;
  }

  readFile(path: string): string | undefined {
    const collaborative = this.collaborativeFile(path);
    if (collaborative) return collaborative.document.toString();
    return this.findEntry(path)?.content?.toString();
  }

  /**
   * The shared text for a file, promoting it to a collaborative document if
   * this user may write and it does not have one yet.
   *
   * Promotion happens here rather than on read because this is the editor's
   * entry point — the one caller that is about to edit. Readers get
   * `undefined` and stay on the `FileStore` path, which still works.
   */
  getText(path: string): YText | undefined {
    // A reader gets nothing and stays on the `FileStore` path. Handing over
    // the `Y.Text` would let CodeMirror mutate a document whose appends are
    // all refused, so the reader would see edits that reach no one and that
    // the flush would then publish as though they were real.
    if (!this.canWrite()) return undefined;
    this.ensureUpdateLog(path);
    return this.collaborativeFile(path)?.document.text;
  }

  writeFile(path: string, content: string): void {
    const entry = this.findEntry(path);
    if (!entry) {
      this.createFileEntry(path, content);
      for (const cb of this.createdCallbacks) cb(path, content);
      this.touch();
      return;
    }

    // Jazz refuses a reader's write to `content` on its own, but nothing
    // refuses a write to a local `Y.Doc`: it would mutate, append nothing,
    // and then publish text no one else will ever have to every listener.
    if (!this.canWrite()) return;

    const collaborative = this.collaborativeFile(path);
    if (collaborative) {
      // A splice against the current state, not a clobbering rewrite: an
      // edit another writer made outside the replaced range survives. The
      // debounced flush publishes the change and refreshes the mirror.
      collaborative.document.setText(content);
    } else {
      entry.content?.$jazz.applyDiff(content);
      this.publish(path, content);
    }
    this.touch();
  }

  deleteFile(path: string): void {
    const files = this.project.files;
    if (!files) return;
    for (let i = 0; i < files.length; i++) {
      const entry = files[i];
      if (entry?.path === path) {
        files.$jazz.splice(i, 1);
        this.disposeCollaborative(path);
        this.lastSyncedContent.delete(path);
        for (const cb of this.deletedCallbacks) cb(path);
        this.touch();
        return;
      }
    }
  }

  onFileChange(callback: FileChangeCallback): Unsubscribe {
    this.changeCallbacks.push(callback);
    this.startSubscription();
    return () => {
      const i = this.changeCallbacks.indexOf(callback);
      if (i !== -1) this.changeCallbacks.splice(i, 1);
      this.stopSubscriptionIfIdle();
    };
  }

  onFileCreated(callback: FileChangeCallback): Unsubscribe {
    this.createdCallbacks.push(callback);
    return () => {
      const i = this.createdCallbacks.indexOf(callback);
      if (i !== -1) this.createdCallbacks.splice(i, 1);
    };
  }

  onFileDeleted(callback: FileDeletedCallback): Unsubscribe {
    this.deletedCallbacks.push(callback);
    return () => {
      const i = this.deletedCallbacks.indexOf(callback);
      if (i !== -1) this.deletedCallbacks.splice(i, 1);
    };
  }

  private canWrite(): boolean {
    const role = this.group.myRole();
    return role === "writer" || role === "admin";
  }

  private touch(): void {
    this.project.$jazz.set("updatedAt", new Date().toISOString());
  }

  /**
   * Give a legacy file an update log, seeded from its current plain text.
   *
   * Two writers can race here: both see no log, both create one, and
   * last-write-wins on the `set` leaves one standing. The loser's client
   * notices on the next notification — `collaborativeFile` keys its cache on
   * the list id — and rebinds to the winner, losing at most the keystrokes
   * it made inside the race window. This is the same trade `ensureAnnotations`
   * documents, and the reason a log is created whole rather than seeded
   * after the fact: two seeds in one log would concatenate the text.
   */
  private ensureUpdateLog(path: string): void {
    const entry = this.findEntry(path);
    if (!entry) return;
    // Three runtime states, and only one of them means "no log":
    // `undefined` is an absent field, `null` is a log that has not loaded
    // yet (the declared type shows only the first). A truthiness check here
    // would treat `null` as absent and create a second log for a file that
    // already has one — which duplicates its text, because two seeds carry
    // two sets of Yjs client ids.
    if (entry.updates !== undefined) return;
    if (!this.canWrite()) return;
    entry.$jazz.set(
      "updates",
      createTextUpdateLog(this.group, entry.content?.toString() ?? ""),
    );
  }

  /**
   * The collaborative document for a path, or undefined when the file has no
   * update log (or it has not loaded yet). Creates the Yjs binding on first
   * use and rebinds it if the underlying list is replaced; never writes.
   */
  private collaborativeFile(path: string): CollaborativeFile | undefined {
    const updates = this.findEntry(path)?.updates;
    const existing = this.collaborative.get(path);
    // A falsy `updates` means either "no log" or "the log has not loaded in
    // this wrapper". Never tear down a document over it: the editor binds
    // CodeMirror to the `Y.Text` and would be left holding an orphan.
    // Documents are released when the file goes away — see `deleteFile` and
    // the sweep in `checkForUpdates`.
    if (!updates) return existing;
    if (existing?.listId === updates.$jazz.id) return existing;
    this.disposeCollaborative(path);

    const log = new JazzUpdateLog(updates, this.group);
    const document = new CollaborativeDocument(log);
    const compactor = new Compactor(document, log, {
      threshold: COMPACTION_THRESHOLD,
      graceMs: COMPACTION_GRACE_MS,
    });
    const file: CollaborativeFile = {
      listId: updates.$jazz.id,
      log,
      document,
      compactor,
      detach: [],
      flushTimer: null,
      hasLocalChange: false,
    };
    file.detach.push(
      document.onChange(() => {
        this.scheduleFlush(path);
      }),
      document.onLocalChange(() => {
        file.hasLocalChange = true;
      }),
    );
    // Every open tab compacts its own logs. Two doing it at once produce two
    // snapshots and one wasted prune; electing a leader would cost more than
    // the duplicate.
    compactor.start();
    this.collaborative.set(path, file);
    return file;
  }

  /** Emit a file's content to listeners, unless they already have it. */
  private publish(path: string, content: string): void {
    if (this.lastSyncedContent.get(path) === content) return;
    this.lastSyncedContent.set(path, content);
    for (const cb of this.changeCallbacks) cb(path, content);
  }

  private scheduleFlush(path: string): void {
    const file = this.collaborative.get(path);
    if (!file) return;
    if (file.flushTimer !== null) clearTimeout(file.flushTimer);
    file.flushTimer = setTimeout(() => {
      file.flushTimer = null;
      this.flush(path);
    }, FLUSH_DELAY_MS);
  }

  /** Publish a collaborative file's current text and refresh its mirror. */
  private flush(path: string): void {
    const file = this.collaborative.get(path);
    if (!file) return;
    const text = file.document.toString();
    const wasLocal = file.hasLocalChange;
    file.hasLocalChange = false;

    this.publish(path, text);

    // Only the writer who made the change refreshes the mirror; every peer
    // doing it would multiply one keystroke into a write per client.
    if (!wasLocal || !this.canWrite()) return;
    this.findEntry(path)?.content?.$jazz.applyDiff(text);
    this.touch();
  }

  private disposeCollaborative(path: string): void {
    const file = this.collaborative.get(path);
    if (!file) return;
    // Forget what listeners were last told. `publish` is content-deduped, and
    // two documents bootstrapped from the same legacy text materialise to the
    // *same* string — so without this a swapped document publishes nothing,
    // the editor never hears `onFileChange`, and it goes on typing into the
    // document being destroyed here. That is silent, total loss of everything
    // written into the orphan, recoverable only if the winner happens to be
    // edited later.
    this.lastSyncedContent.delete(path);
    if (file.flushTimer !== null) clearTimeout(file.flushTimer);
    for (const detach of file.detach) detach();
    file.compactor.stop();
    file.document.destroy();
    file.log.destroy();
    this.collaborative.delete(path);
  }

  /**
   * Reconcile against the project after a Jazz notification: pick up files
   * that just gained a log, and emit changes for the ones that have not.
   *
   * A collaborative file is skipped: its own log subscription drives its
   * change events, and its plain text is a lagging mirror that would emit
   * stale content if read here.
   */
  private checkForUpdates(): void {
    const files = this.project.files;
    if (!files) return;
    const present = new Set<string>();
    for (let i = 0; i < files.length; i++) {
      const entry = files[i];
      if (!entry?.path) continue;
      present.add(entry.path);

      const collaborative = this.collaborativeFile(entry.path);
      if (collaborative) {
        // The log subscription drives this file's ongoing changes; this
        // publishes the file the first time it is seen, which is how a
        // remote writer's newly created file reaches the listeners.
        this.publish(entry.path, collaborative.document.toString());
        continue;
      }

      const content = entry.content?.toString();
      if (content === undefined) continue;
      this.publish(entry.path, content);
    }

    for (const path of [...this.collaborative.keys()]) {
      if (!present.has(path)) this.disposeCollaborative(path);
    }
  }

  private startSubscription(): void {
    if (this.unsubscribeJazz) return;
    this.unsubscribeJazz = this.project.$jazz.subscribe(
      // `updates` is resolved so an absent log is distinguishable from one
      // that simply has not arrived — see `ensureUpdateLog`. Resolving an
      // optional field no file has is not an error; those entries just keep
      // reporting `undefined`.
      {
        resolve: {
          files: { $each: { content: true, updates: { $each: true } } },
        },
      },
      () => {
        this.checkForUpdates();
      },
    );
  }

  private stopSubscriptionIfIdle(): void {
    if (this.changeCallbacks.length > 0) return;
    this.unsubscribeJazz?.();
    this.unsubscribeJazz = null;
  }

  private findEntry(path: string): co.loaded<typeof FileEntry> | undefined {
    const files = this.project.files;
    if (!files) return undefined;
    for (let i = 0; i < files.length; i++) {
      const entry = files[i];
      if (entry?.path === path) return entry;
    }
    return undefined;
  }

  /**
   * New files are born collaborative when the creator may write, so the
   * bootstrap race only ever applies to files that predate the update log.
   */
  private createFileEntry(path: string, content: string): void {
    const files = this.project.files;
    if (!files) return;
    const owner = this.group;
    const entry = FileEntry.create(
      {
        path,
        content: co.plainText().create(content, { owner }),
        ...(this.canWrite() && {
          updates: createTextUpdateLog(owner, content),
        }),
      },
      { owner },
    );
    files.$jazz.push(entry);
  }
}
